// Function index:
//   DiceRollerMount        — controller returned by mountDiceRoller.
//   mountDiceRoller(ws?)   — wire the 🎲 button + roll popover to player.roll.
//
// Player-initiated transparent dice. The button toggles a small popover with
// an expression field (default 1d20) and an optional purpose; rolling sends a
// player.roll WS event the server validates + broadcasts as combat.dice_rolled
// (rendered by the existing chat.dice handler in game/ws-bindings.ts). No Opus
// call — zero token cost — so the button stays enabled even during combat.

import type { WS } from './ws.js';

export interface DiceRollerMount {
  attachWS(ws: WS): void;
}

export function mountDiceRoller(initial: WS | null = null): DiceRollerMount {
  const btn = document.getElementById('dice-btn') as HTMLButtonElement;
  const popover = document.getElementById('dice-popover') as HTMLDivElement;
  const expr = document.getElementById('dice-expr') as HTMLInputElement;
  const purpose = document.getElementById('dice-purpose') as HTMLInputElement;
  const roll = document.getElementById('dice-roll') as HTMLButtonElement;
  let ws: WS | null = initial;

  const close = (): void => { popover.hidden = true; btn.classList.remove('open'); };
  const open = (): void => {
    popover.hidden = false;
    btn.classList.add('open');
    expr.focus();
    expr.select();
  };

  btn.addEventListener('click', () => { if (popover.hidden) open(); else close(); });

  const send = (): void => {
    const expression = expr.value.trim();
    if (!expression || !ws) return;
    const p = purpose.value.trim();
    ws.send('player.roll', { expression, purpose: p || undefined });
    purpose.value = '';
    close();
  };

  roll.addEventListener('click', send);
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') { e.preventDefault(); send(); }
    else if (e.key === 'Escape') { close(); }
  };
  expr.addEventListener('keydown', onKey);
  purpose.addEventListener('keydown', onKey);

  // Click outside the popover (and outside the toggle button) closes it.
  document.addEventListener('pointerdown', (e) => {
    if (popover.hidden) return;
    const t = e.target as Node;
    if (popover.contains(t) || btn.contains(t)) return;
    close();
  });

  return { attachWS: (newWs) => { ws = newWs; } };
}
